import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo } from "react";

import { useAdditionalFields } from "../hooks/addition-fields";
import { useLocale } from "../stores/locale";
import { AdditionalFields } from "./additional-fields";
import { Button } from "./button";
import { Input } from "./input";

export type ApartmentFormValues = {
  index: string;
  objectNumber: string;
  streetNumber: string;
  apartmentNumber: string;
  floorNumber: string;
  area: string;
  share: string;
  additionalFields?: Record<string, string>;
};

export function ApartmentForm({
  onCancel,
  onChange,
  onSubmit,
  values,
  loading = false,
  disabled = false,
  hasError = false,
}: {
  onCancel: () => void;
  onChange: (values: ApartmentFormValues) => void;
  onSubmit: () => void;
  values: ApartmentFormValues;
  loading?: boolean;
  disabled?: boolean;
  hasError?: boolean;
}) {
  const t = useLocale((state) => state.locale);
  const { data: additionalFields } = useAdditionalFields("apartment");

  const isValid = useMemo(() => {
    return (
      values.index.trim() !== "" &&
      additionalFields.every(
        (field) =>
          !field.required ||
          values.additionalFields?.[field.name]?.trim() !== "",
      )
    );
  }, [values, additionalFields]);
  const isDisabled = useMemo(() => {
    return disabled || loading;
  }, [disabled, loading]);

  return (
    <form
      className="flex-1 flex flex-col justify-between border-1 border-gray-300 rounded-xs p-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="grid grid-cols-2 gap-2">
        <Input
          required
          disabled={isDisabled}
          label={t.apartment.index}
          type="number"
          value={values.index}
          onChange={(e) => onChange({ ...values, index: e.target.value })}
        />
        <Input
          disabled={isDisabled}
          label={t.apartment.objectNumber}
          type="text"
          value={values.objectNumber}
          onChange={(e) =>
            onChange({ ...values, objectNumber: e.target.value })
          }
        />
        <Input
          disabled={isDisabled}
          label={t.apartment.streetNumber}
          type="text"
          value={values.streetNumber}
          onChange={(e) =>
            onChange({ ...values, streetNumber: e.target.value })
          }
        />
        <Input
          disabled={isDisabled}
          label={t.apartment.apartmentNumber}
          type="text"
          value={values.apartmentNumber}
          onChange={(e) =>
            onChange({
              ...values,
              apartmentNumber: e.target.value,
            })
          }
        />
        <Input
          disabled={isDisabled}
          label={t.apartment.floorNumber}
          type="text"
          value={values.floorNumber}
          onChange={(e) => onChange({ ...values, floorNumber: e.target.value })}
        />
        <Input
          disabled={isDisabled}
          label={t.apartment.area}
          type="number"
          value={values.area}
          onChange={(e) => onChange({ ...values, area: e.target.value })}
        />
        <Input
          disabled={isDisabled}
          label={t.apartment.share}
          type="number"
          value={values.share}
          onChange={(e) => onChange({ ...values, share: e.target.value })}
        />
        <AdditionalFields
          additionalFields={additionalFields}
          disabled={isDisabled}
          values={values.additionalFields ?? {}}
          onChange={(additionalFieldValues) =>
            onChange({
              ...values,
              additionalFields: {
                ...additionalFieldValues,
              },
            })
          }
        />
      </div>
      <div className="flex gap-2 justify-end">
        {hasError && (
          <p className="text-red-500 flex items-center gap-2">
            <FontAwesomeIcon icon={faTriangleExclamation} />
            {t.apartment.error}
          </p>
        )}
        <Button disabled={disabled} severity="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={disabled || !isValid} loading={loading} type="submit">
          Save
        </Button>
      </div>
    </form>
  );
}
